// FLAGS: none
// What the scheduler has typed for each non-source language, shared by the create and edit forms.
import { ContentLanguage } from "@src/models/spaces";

/** One session's copy inside a translation draft. Blank fields fall back to the source copy. */
export interface TranslationSessionDraft {
  name: string;
  sessionInstructions: string;
}

/**
 * Everything the scheduler has typed for one non-source language: the event's
 * copy plus each session's, keyed by session. Kept whole (rather than a flat
 * list) so the panel can render source-beside-translation without lookups.
 *
 * The session key is whatever identifies a session to the form holding the
 * draft: its id when editing, and its client-side draft key when creating,
 * where no id exists until the create request runs.
 */
export interface TranslationDraft {
  languageCode: ContentLanguage;
  title: string;
  description: string;
  sessions: Record<string, TranslationSessionDraft>;
}

/** A blank draft with a slot per session, so every session renders a row to type into. */
export const emptyDraft = (
  languageCode: ContentLanguage,
  sessionKeys: readonly string[],
): TranslationDraft => ({
  languageCode,
  title: "",
  description: "",
  sessions: Object.fromEntries(
    sessionKeys.map((key) => [key, { name: "", sessionInstructions: "" }]),
  ),
});

/** How much of a language has been filled in, so a half-done one is visible at a glance. */
export const filledCount = (draft: TranslationDraft): number =>
  [
    draft.title,
    draft.description,
    ...Object.values(draft.sessions).flatMap((session) => [
      session.name,
      session.sessionInstructions,
    ]),
  ].filter((value) => value.trim() !== "").length;

/**
 * Drops what carries nothing and blanks-to-null what remains, which is how an
 * opened-but-unfilled language panel removes itself instead of storing empty
 * rows. Blank-to-null keeps the per-field fallback working: a whitespace title
 * must defer to the source copy rather than blank the page.
 */
export const toEventTranslationPayload = (
  drafts: readonly TranslationDraft[],
) =>
  drafts
    .map((draft) => ({
      languageCode: draft.languageCode,
      title: draft.title.trim() || null,
      description: draft.description.trim() || null,
    }))
    .filter((entry) => entry.title !== null || entry.description !== null);

/**
 * The per-session copy for one session, across every language. The drafts are
 * keyed language → sessions and the create payload wants session → languages,
 * so this is the inversion.
 */
export const toSessionTranslationPayload = (
  drafts: readonly TranslationDraft[],
  sessionKey: string,
) =>
  drafts
    .map((draft) => ({
      languageCode: draft.languageCode,
      title: draft.sessions[sessionKey]?.name.trim() || null,
      sessionInstructions:
        draft.sessions[sessionKey]?.sessionInstructions.trim() || null,
    }))
    .filter(
      (entry) => entry.title !== null || entry.sessionInstructions !== null,
    );
